/**
 * 태어난 시각 고르기 (귀인도감 client/src/components/pickers/TimeField.jsx 방식, 사용자 요청 2026-10-14, D79).
 *
 * 오전/오후 · 시(1~12) · 분(0~59) 세 다이얼. 값은 24시간 'HH:MM' 으로 두고 다이얼만 12시간제로 보인다
 * (「오전 12시」가 0시인지 12시인지 헷갈리면 2시간 단위 시주가 바뀜. 오전 12시 = 0시, 오후 12시 = 정오).
 * - 다이얼: 세로 스크롤 + 칸에 딱 멈춤(scroll-snap). 누르면 그 값으로 가운데로, 굴려 멈춘 칸도 값
 * - 분은 60칸 그대로(서울 진태양시 보정 −32분처럼 시주 경계가 5의 배수가 아닌 분에 걸림)
 * - 위 미리 보기: 큰 시각 + 「묘시」(서울 진태양시 · 양력 생일이면 균시차까지, 음력이면 숨김)
 * - 값이 없으면 오전 6시부터
 */
import { useEffect, useRef, useState } from 'react';
import { PickerDialog, PickerField } from './PickerDialog.jsx';
import { branchOfTime } from './solarTime.js';

const MERIDIEMS = [
    { value: 'am', label: '오전' },
    { value: 'pm', label: '오후' },
];
const HOURS12 = Array.from({ length: 12 }, (_, index) => ({
    value: index + 1,
    label: String(index + 1).padStart(2, '0'),
}));
const MINUTES60 = Array.from({ length: 60 }, (_, index) => ({ value: index, label: String(index).padStart(2, '0') }));
const ITEM_H = 40;
const VISIBLE_ROWS = 5;
const HALF = Math.floor(VISIBLE_ROWS / 2);
const SETTLE_MS = 80;

const pad = (number) => String(number).padStart(2, '0');

export function parseBirthTime(value) {
    const match = /^(\d{1,2}):(\d{2})$/.exec(String(value ?? ''));
    if (!match) {
        return null;
    }
    const hour = Number(match[1]);
    const minute = Number(match[2]);
    return hour <= 23 && minute <= 59 ? { hour, minute } : null;
}

export function formatBirthTime({ hour, minute }) {
    return `${pad(hour)}:${pad(minute)}`;
}

const meridiemOf = (hour) => (hour < 12 ? 'am' : 'pm');
const meridiemLabel = (hour) => (hour < 12 ? '오전' : '오후');

/** 「오후 22:40」 */
export function birthTimeLabel(parts) {
    return `${meridiemLabel(parts.hour)} ${formatBirthTime(parts)}`;
}

/** 24시간 → 12시간(1~12) */
function to12(hour24) {
    const hour = hour24 % 12;
    return hour === 0 ? 12 : hour;
}

/** 오전/오후 + 12시간 → 24시간. 오전 12시 = 0시, 오후 12시 = 12시 */
function to24(meridiem, hour12) {
    const hour = hour12 % 12;
    return meridiem === 'pm' ? hour + 12 : hour;
}

/**
 * 폼 안의 태어난 시각 칸
 * @param {{ value: string, onChange: (value: string) => void, disabled?: boolean, birthDate?: string|null }} props
 *   birthDate 는 양력 생일일 때만(균시차로 시주 미리 보기). 음력이면 null
 */
export function BirthTimeField({ value, onChange, disabled = false, birthDate = null, label = '태어난 시간' }) {
    const [open, setOpen] = useState(false);
    const selected = parseBirthTime(value);
    return (
        <>
            <PickerField
                label={label}
                labelId="birth-time-label"
                valueText={selected && !disabled ? birthTimeLabel(selected) : null}
                placeholder="태어난 시각 선택"
                disabled={disabled}
                onOpen={() => setOpen(true)}
            />
            {open && !disabled ? (
                <BirthTimeSheet
                    initial={selected ?? { hour: 6, minute: 0 }}
                    birthDate={birthDate}
                    onClose={() => setOpen(false)}
                    onConfirm={(parts) => {
                        onChange(formatBirthTime(parts));
                        setOpen(false);
                    }}
                />
            ) : null}
        </>
    );
}

export function BirthTimeSheet({ initial, birthDate = null, onClose, onConfirm }) {
    const [draft, setDraft] = useState({ hour: initial.hour, minute: initial.minute });
    const setMeridiem = (meridiem) => setDraft((d) => ({ ...d, hour: to24(meridiem, to12(d.hour)) }));
    const setHour12 = (hour12) => setDraft((d) => ({ ...d, hour: to24(meridiemOf(d.hour), hour12) }));
    const setMinute = (minute) => setDraft((d) => ({ ...d, minute }));

    return (
        <PickerDialog
            eyebrow="시각"
            title="태어난 시각"
            ariaLabel="태어난 시각 고르기"
            confirmLabel={`${birthTimeLabel(draft)} 선택`}
            onConfirm={() => onConfirm(draft)}
            onClose={onClose}
        >
            <div className="flex items-baseline gap-2.5 rounded-xl border border-hairline bg-surface-sunken px-3.5 py-3">
                <span className="self-center rounded-md bg-brand px-2 py-0.5 text-[12px] font-semibold text-on-brand">
                    {meridiemLabel(draft.hour)}
                </span>
                {/* 글자 크기는 생년월일 고르기(날짜 칸 16px · 칩 13px)와 같게(2026-10-18 사용자) */}
                <span className="text-[16px] leading-none font-semibold tabular-nums">{formatBirthTime(draft)}</span>
                {birthDate ? (
                    <span className="ml-auto self-center text-[13px] text-text-secondary">
                        {branchOfTime(draft.hour, draft.minute, birthDate)}시
                    </span>
                ) : null}
            </div>
            <div className="relative">
                {/* 가운데 선택 줄(장식) */}
                <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-x-0 border-y border-brand/60 bg-brand/10"
                    style={{ top: ITEM_H * HALF, height: ITEM_H }}
                />
                <div className="flex justify-center gap-5">
                    <Dial
                        ariaLabel="오전/오후"
                        items={MERIDIEMS}
                        value={meridiemOf(draft.hour)}
                        onChange={setMeridiem}
                        width={76}
                    />
                    <Dial ariaLabel="시" items={HOURS12} value={to12(draft.hour)} onChange={setHour12} width={68} />
                    <Dial ariaLabel="분" items={MINUTES60} value={draft.minute} onChange={setMinute} width={68} />
                </div>
            </div>
        </PickerDialog>
    );
}

/**
 * 세로 스크롤 다이얼. 누르면 그 값으로 가운데로 스크롤, 손가락으로 굴려 멈춘 칸도 값으로(scroll-snap 이 칸에 맞춰 멈춤)
 */
function Dial({ items, value, onChange, ariaLabel, width }) {
    const scrollRef = useRef(null);
    const settleRef = useRef(null);
    const selectedIndex = items.findIndex((item) => item.value === value);

    // 열 때 고른 값이 가운데에(애니메이션 없이)
    useEffect(() => {
        if (scrollRef.current && selectedIndex >= 0) {
            scrollRef.current.scrollTop = selectedIndex * ITEM_H;
        }
        // 열 때 한 번
    }, []);
    useEffect(() => () => clearTimeout(settleRef.current), []);

    function scrollToIndex(index, smooth = true) {
        const box = scrollRef.current;
        if (!box) {
            return;
        }
        if (typeof box.scrollTo === 'function') {
            box.scrollTo({ top: index * ITEM_H, behavior: smooth ? 'smooth' : 'auto' });
        } else {
            box.scrollTop = index * ITEM_H;
        }
    }

    function pick(index) {
        scrollToIndex(index);
        onChange(items[index].value);
    }

    function handleScroll() {
        clearTimeout(settleRef.current);
        settleRef.current = setTimeout(() => {
            const box = scrollRef.current;
            if (!box) {
                return;
            }
            const index = Math.min(items.length - 1, Math.max(0, Math.round(box.scrollTop / ITEM_H)));
            if (items[index] && items[index].value !== value) {
                onChange(items[index].value);
            }
        }, SETTLE_MS);
    }

    return (
        <div
            ref={scrollRef}
            role="group"
            aria-label={ariaLabel}
            onScroll={handleScroll}
            className="snap-y snap-mandatory overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            style={{
                width,
                height: ITEM_H * VISIBLE_ROWS,
                maskImage:
                    'linear-gradient(to bottom, transparent 0, black 40px, black calc(100% - 40px), transparent 100%)',
                WebkitMaskImage:
                    'linear-gradient(to bottom, transparent 0, black 40px, black calc(100% - 40px), transparent 100%)',
            }}
        >
            <div style={{ height: ITEM_H * HALF }} />
            {items.map((item, index) => {
                const active = item.value === value;
                return (
                    <button
                        key={item.value}
                        type="button"
                        aria-pressed={active}
                        onClick={() => pick(index)}
                        className={`flex w-full snap-center items-center justify-center tabular-nums ${
                            active ? 'text-[16px] font-semibold text-text-primary' : 'text-[16px] text-text-tertiary'
                        }`}
                        style={{ height: ITEM_H }}
                    >
                        {item.label}
                    </button>
                );
            })}
            <div style={{ height: ITEM_H * HALF }} />
        </div>
    );
}
